import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import LineChartWithLimits from "./LineChartWithLimits";
import {
	getCardBorderStyle,
	getSurfaceBorderColor,
	getSurfaceBorderStyle,
} from "@/app/application/theme";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { UserContext, TranslationContext } from "@/app/providers";
import { stringAvatar } from "@/features/procedures/lib/checklist";
import { AlertConfirmDeleteMeasurement } from "@/shared/notifications/AlertConfirm";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { AlertToast } from "@/shared/notifications/AlertToast";
import {
	getAlertTheme,
	withAlertTheme,
} from "@/shared/notifications/alertTheme";
import { StorageKeys } from "@/storage/storageKeys";
import {
	CloseActionButton,
	DeleteActionButton,
	SaveActionButton,
} from "@/shared/ui";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	Avatar,
	Box,
	Button,
	Chip,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	List,
	ListItem,
	Paper,
	Skeleton,
	Slide,
	type SlideProps,
	Stack,
	TextField,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Swal from "sweetalert2";

import { useFetchUsers } from "@/service/ApplicationQueries";
import {
	useDeleteLabMeasurement,
	useMutateCreateComment,
	useUpdateMeasurement,
} from "@/service/LabHomeMutations";
import {
	useFetchMeasurementAnalyzeById,
	useFetchMeasurementById,
} from "@/service/LabHomeQueries";
import { useLocalStorage } from "@/service/Utilities";
import { Language } from "@/model/Language/Language";


dayjs.extend(utc);
dayjs.extend(timezonePlugin);

dayjs.extend(utc);

const Transition = React.forwardRef(function Transition(
	props: SlideProps,
	ref: React.Ref<unknown>,
) {
	return <Slide direction="up" ref={ref} {...props} />;
});

interface LabMeasurementDialogProps {
	open: boolean;
	onClose: () => void;
	LabMeasurementId: number | null;
	LabAnalyzeId: number | null;
}

interface Measurement {
	LabMeasurementId: number;
	LabAnalyzeId: number;
	GroupId: number;
	GroupName: string;
	Parameter: number;
	TagId: string;
	TagDescription: string;
	ParameterName: string;
	Measurement: number | null;
	MinLimit: number;
	MaxLimit: number;
	TestDate: string;
	SaveDate: string;
	UserId: number;
	Comment: string;
	Comments: MeasurementComment[];
}

interface MeasurementListProps {
	measurements: Measurement[];
	onSelect: (LabMeasurementId: number, LabAnalyzeId: number) => void;
	selectedLabMeasurementId: number | null;
}

interface MeasurementComment {
	CommentId: number;
	Comment: string;
	Date: string;
	UserId: number;
	UserName: string;
}

interface LabMeasurementDetailsProps {
	data: Measurement;
	measurementAnalyzeIdData: Measurement[];
	editableMeasurement: number | string;
	setEditableMeasurement: React.Dispatch<React.SetStateAction<number | string>>;
	editableTestDate: string;
	setEditableTestDate: React.Dispatch<React.SetStateAction<string>>;
	editableComment: string;
	setEditableComment: React.Dispatch<React.SetStateAction<string>>;
	editableSaveDate: string;
	setEditableSaveDate: React.Dispatch<React.SetStateAction<string>>;
}

interface CommentFormProps {
	labMeasurementId: number;
	setComments: React.Dispatch<React.SetStateAction<MeasurementComment[]>>;
}

interface CommentListProps {
	comments: MeasurementComment[];
	timezone: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

const toNumber = (value: unknown, fallback = 0): number => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value: unknown): number | null => {
	if (value === null || value === undefined || value === "") return null;
	if (typeof value === "number" && Number.isFinite(value)) return value;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const toStringValue = (value: unknown, fallback = ""): string =>
	typeof value === "string" ? value : fallback;

const toDateIsoString = (value: unknown, fallback: string): string => {
	if (typeof value === "string" && dayjs(value).isValid()) {
		return value;
	}

	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		return value.toISOString();
	}

	return fallback;
};

const toMeasurementComment = (value: unknown): MeasurementComment | null => {
	if (!isRecord(value)) {
		return null;
	}

	const nowIso = new Date().toISOString();

	return {
		CommentId: toNumber(value.CommentId),
		Comment: toStringValue(value.Comment),
		Date: toDateIsoString(value.Date, nowIso),
		UserId: toNumber(value.UserId),
		UserName: toStringValue(value.UserName, "Unknown User"),
	};
};

const toMeasurement = (value: unknown): Measurement | null => {
	if (!isRecord(value)) {
		return null;
	}

	const nowIso = new Date().toISOString();
	const rawComments = Array.isArray(value.Comments) ? value.Comments : [];
	const comments = rawComments
		.map(toMeasurementComment)
		.filter((comment): comment is MeasurementComment => comment !== null);

	return {
		LabMeasurementId: toNumber(value.LabMeasurementId),
		LabAnalyzeId: toNumber(value.LabAnalyzeId),
		GroupId: toNumber(value.GroupId),
		GroupName: toStringValue(value.GroupName),
		Parameter: toNumber(value.Parameter),
		TagId: toStringValue(value.TagId),
		TagDescription: toStringValue(value.TagDescription),
		ParameterName: toStringValue(value.ParameterName),
		Measurement: toNullableNumber(value.Measurement),
		MinLimit: toNumber(value.MinLimit),
		MaxLimit: toNumber(value.MaxLimit),
		TestDate: toDateIsoString(value.TestDate, nowIso),
		SaveDate: toDateIsoString(value.SaveDate, nowIso),
		UserId: toNumber(value.UserId),
		Comment: toStringValue(value.Comment),
		Comments: comments,
	};
};

const toMeasurementArray = (value: unknown): Measurement[] =>
	Array.isArray(value)
		? value
				.map(toMeasurement)
				.filter((item): item is Measurement => item !== null)
		: [];

const getErrorMessage = (error: unknown, fallback: string): string => {
	if (isRecord(error)) {
		const response = isRecord(error.response) ? error.response : undefined;
		const data =
			response && isRecord(response.data) ? response.data : undefined;
		const responseMessage = data ? data.message : undefined;
		if (typeof responseMessage === "string" && responseMessage.trim()) {
			return responseMessage;
		}

		const message = error.message;
		if (typeof message === "string" && message.trim()) {
			return message;
		}
	}

	if (error instanceof Error && error.message.trim()) {
		return error.message;
	}

	return fallback;
};

/**
 * LabMeasurementDialog component is a React functional component that renders a dialog
 * for displaying and editing lab measurement details. It fetches measurement data based
 * on provided IDs and allows users to save or delete measurements.
 *
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.open - Determines if the dialog is open
 * @param {function} props.onClose - Function to call when the dialog is closed
 * @param {number} props.LabMeasurementId - ID of the lab measurement to fetch
 * @param {number} props.LabAnalyzeId - ID of the lab analyze to fetch
 *
 * @returns {JSX.Element} The rendered component
 *
 * @example
 * <LabMeasurementDialog
 *   open={isOpen}
 *   onClose={handleClose}
 *   LabMeasurementId={123}
 *   LabAnalyzeId={456}
 * />
 *
 * @remarks
 * This component uses several hooks to fetch and manage data:
 * - `useFetchMeasurementById` to fetch measurement data by ID
 * - `useFetchMeasurementAnalyzeById` to fetch analyze data by ID
 * - `useUpdateMeasurement` to update measurement data
 * - `useDeleteLabMeasurement` to delete measurement data
 *
 * The component also manages several pieces of state to handle editable fields and selected items.
 * It uses `dayjs` for date manipulation and `AlertToast` for displaying success or error messages.
 */
const LabMeasurementDialog: React.FC<LabMeasurementDialogProps> = ({
	open,
	onClose,
	LabMeasurementId,
	LabAnalyzeId,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const [editableMeasurement, setEditableMeasurement] = useState<
		number | string
	>("");
	const [editableTestDate, setEditableTestDate] = useState<string>(() =>
		new Date().toISOString(),
	);
	const [editableSaveDate, setEditableSaveDate] = useState<string>(() =>
		new Date().toISOString(),
	);
	const [editableComment, setEditableComment] = useState("");
	const [LabMeasurementIdIs, setLabMeasurementId] = useState<number | null>(
		null,
	);
	const [LabAnalyzeIdIs, setLabAnalyzeId] = useState<number | null>(null);
	const [selectedItem, setSelectedItem] = useState<Measurement | null>(null);
	const theme = useTheme();

	const updateMeasurementMutation = useUpdateMeasurement();
	const deleteMutation = useDeleteLabMeasurement();
	const isSavingMeasurement = updateMeasurementMutation.isPending;
	const isDeletingMeasurement = deleteMutation.isPending;
	const isMeasurementActionPending =
		isSavingMeasurement || isDeletingMeasurement;
	const measurementByIdQuery = useFetchMeasurementById(LabMeasurementIdIs);
	const measurementAnalyzeByIdQuery =
		useFetchMeasurementAnalyzeById(LabAnalyzeIdIs);

	const measurementIdData = useMemo(
		() => toMeasurement(measurementByIdQuery.data),
		[measurementByIdQuery.data],
	);

	const measurementAnalyzeIdData = useMemo(
		() => toMeasurementArray(measurementAnalyzeByIdQuery.data),
		[measurementAnalyzeByIdQuery.data],
	);
	const { isLoading, error } = measurementByIdQuery;

	useEffect(() => {
		if (LabMeasurementId !== null || LabAnalyzeId !== null) {
			setLabMeasurementId(LabMeasurementId);
			setLabAnalyzeId(LabAnalyzeId);
		}
	}, [LabMeasurementId, LabAnalyzeId]);

	useEffect(() => {
		if (LabMeasurementIdIs === null && measurementAnalyzeIdData.length > 0) {
			const firstMeasurement = measurementAnalyzeIdData[0];
			setLabMeasurementId(firstMeasurement.LabMeasurementId);
			setLabAnalyzeId(firstMeasurement.LabAnalyzeId);
			setSelectedItem(firstMeasurement);
		}
	}, [measurementAnalyzeIdData, LabMeasurementIdIs]);

	// Initialize editable fields once data is fetched
	useEffect(() => {
		if (measurementIdData) {
			setEditableMeasurement(measurementIdData.Measurement ?? "");
			setEditableTestDate(measurementIdData.TestDate);
			setEditableComment(measurementIdData.Comment || "");
			setEditableSaveDate(measurementIdData.SaveDate);
			setSelectedItem(measurementIdData);
		}
	}, [measurementIdData]);

	const { timezone } = useTime();

	// Memoize parsed measurement value
	const parsedMeasurement = useMemo(() => {
		try {
			const value = parseFloat(String(editableMeasurement).replace(",", "."));
			return isNaN(value) ? null : value;
		} catch {
			return null;
		}
	}, [editableMeasurement]);

	// Handle saving data with validation and error handling
	const handleSave = useCallback(async () => {
		if (isMeasurementActionPending) {
			return;
		}

		if (!selectedItem) {
			void AlertToast.failed("No measurement selected");
			return;
		}

		if (parsedMeasurement === null && !editableComment.trim()) {
			void AlertToast.failed("Please enter a measurement value or a comment");
			return;
		}

		// Validate measurement is within limits
		if (
			parsedMeasurement !== null &&
			(parsedMeasurement < selectedItem.MinLimit ||
				parsedMeasurement > selectedItem.MaxLimit)
		) {
			const theme = getAlertTheme();
			const result = await Swal.fire(
				withAlertTheme(
					{
						title: "Warning",
						text: `The measurement value (${parsedMeasurement}) is outside the allowed range (${selectedItem.MinLimit} - ${selectedItem.MaxLimit}). Do you want to save anyway?`,
						icon: "warning",
						showCancelButton: true,
						confirmButtonColor: theme.confirmColor,
						cancelButtonColor: theme.cancelColor,
						confirmButtonText: "Yes, save anyway",
						cancelButtonText: "Cancel",
					},
					theme,
				),
			);
			if (!result.isConfirmed) {
				return;
			}
		}

		const activeLabMeasurementId =
			LabMeasurementIdIs ?? selectedItem.LabMeasurementId;
		const activeLabAnalyzeId = LabAnalyzeIdIs ?? selectedItem.LabAnalyzeId;

		const payload = {
			labMeasurementId: activeLabMeasurementId,
			labAnalyzeId: activeLabAnalyzeId,
			groupId: selectedItem.GroupId,
			tagId: selectedItem.TagId,
			measurement: parsedMeasurement,
			testDate: dayjs.utc(editableTestDate).tz(timezone).toISOString(),
			saveDate: dayjs.utc(editableSaveDate).tz(timezone).toISOString(),
			comment: editableComment || "",
			parameter: selectedItem.Parameter,
			measurementType: "SpotSample",
			exportToPi: true,
			analyseType: "Default",
		};

		try {
			await updateMeasurementMutation.mutateAsync(payload);
			void AlertToast.success("Measurement saved successfully");
			onClose();
		} catch (error: unknown) {
			console.error("Error updating measurement:", error);
			void AlertToast.failed(
				getErrorMessage(error, "Failed to save measurement"),
			);
		}
	}, [
		selectedItem,
		parsedMeasurement,
		LabMeasurementIdIs,
		LabAnalyzeIdIs,
		editableTestDate,
		editableSaveDate,
		editableComment,
		timezone,
		updateMeasurementMutation,
		isMeasurementActionPending,
		onClose,
	]);

	// Save on enter key press
	useEffect(() => {
		if (!open) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
				event.preventDefault();
				void handleSave();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [open, handleSave]);

	const handleListItemClick = useCallback(
		(selectedLabMeasurementId: number, selectedLabAnalyzeId: number) => {
			if (
				LabMeasurementIdIs !== selectedLabMeasurementId &&
				measurementAnalyzeIdData
			) {
				setLabMeasurementId(selectedLabMeasurementId);
				setLabAnalyzeId(selectedLabAnalyzeId);
				const selectedData = measurementAnalyzeIdData.find(
					(item) => item.LabMeasurementId === selectedLabMeasurementId,
				);
				if (selectedData) {
					setSelectedItem(selectedData);
				}
			}
		},
		[LabMeasurementIdIs, measurementAnalyzeIdData],
	);

	const handleClose = useCallback(() => {
		if (isMeasurementActionPending) {
			return;
		}
		setLabMeasurementId(null);
		setLabAnalyzeId(null);
		setSelectedItem(null);
		setEditableMeasurement(0);
		setEditableTestDate(new Date().toISOString());
		setEditableSaveDate(new Date().toISOString());
		setEditableComment("");
		onClose();
	}, [isMeasurementActionPending, onClose]);

	const handleDelete = useCallback(async () => {
		if (isMeasurementActionPending) {
			return;
		}

		if (!selectedItem?.LabMeasurementId) {
			void AlertToast.failed("No measurement selected for deletion");
			return;
		}

		try {
			await deleteMutation.mutateAsync(selectedItem.LabMeasurementId);
			void AlertToast.success("Measurement deleted successfully");
			onClose();
		} catch (error: unknown) {
			console.error("Error deleting measurement:", error);
			void AlertToast.failed(
				getErrorMessage(error, "Failed to delete measurement"),
			);
		}
	}, [isMeasurementActionPending, selectedItem, deleteMutation, onClose]);

	const handleSaveClick = useCallback(() => {
		void handleSave();
	}, [handleSave]);

	const handleDeleteClick = useCallback(() => {
		if (isMeasurementActionPending) {
			return;
		}
		void AlertConfirmDeleteMeasurement(() => {
			void handleDelete();
		}, theme.palette.mode === "light");
	}, [handleDelete, isMeasurementActionPending, theme.palette.mode]);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="lg"
			fullWidth
			fullScreen={false}
			aria-labelledby="lab-measurement-dialog-title"
			TransitionComponent={Transition}
			keepMounted
			PaperProps={{
				sx: {
					borderRadius: 2.5,
					overflow: "hidden",
					border: `1px solid ${theme.palette.divider}`,
					bgcolor: "background.paper",
					maxHeight: "90vh",
					height: "90vh",
					display: "flex",
					flexDirection: "column",
					boxShadow:
						theme.palette.mode === "dark"
							? `0 24px 64px ${alpha(theme.palette.common.black, 0.42)}`
							: `0 24px 64px ${alpha(theme.palette.common.black, 0.16)}`,
				},
			}}
		>
			<DialogTitle
				id="lab-measurement-dialog-title"
				component="div"
				sx={{
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 1.75,
					pb: 1.5,
					borderBottom: 1,
					borderColor: "divider",
					bgcolor:
						theme.palette.mode === "dark"
							? alpha(theme.palette.common.white, 0.03)
							: "grey.50",
					display: "flex",
					flexDirection: "column",
					gap: 0.4,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						textTransform: "uppercase",
						pr: 5,
					}}
				>
					<Translate String="Auto.Features.Lab.Ui.Components.LabMeasurementDialog.L609C6.LabMeasurement">Lab measurement</Translate>
				</Typography>
				{selectedItem && (
					<Typography
						variant="body2"
						sx={{
							color: "text.secondary",
						maxWidth: 520,
						whiteSpace: "nowrap",
						textOverflow: "ellipsis",
						overflow: "hidden",
						fontWeight: 500,
					}}
				>
						{selectedItem.TagDescription || selectedItem.GroupName} •{" "}
						<Translate String="LabMeasurementDialog.Tag">Tag</Translate>{" "}
						{selectedItem.TagId}
					</Typography>
				)}
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.LabMeasurementDialog.L630C17.Close", "Close")}
					onClick={handleClose}
					size="small"
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: theme.palette.text.secondary,
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
						bgcolor: "background.paper",
						"&:hover": {
							color: theme.palette.text.primary,
							backgroundColor: theme.palette.action.hover,
						},
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			{isLoading ? (
				<DialogContent
					sx={{
						bgcolor: "background.default",
						px: { xs: 2, sm: 2.5 },
						pt: 2,
						pb: 0,
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					}}
				>
					<Grid
						container
						spacing={1.5}
						sx={{ height: "100%", minHeight: 0, flex: 1 }}
					>
						{/* Left Side - Details Skeleton */}
						<Grid
							item
							xs={12}
							md={8}
							sx={{
								display: "flex",
								flexDirection: "column",
								minHeight: 0,
								height: "100%",
							}}
						>
							<Paper
								sx={(t) => ({
									p: { xs: 2, sm: 2.5 },
									borderRadius: 2.5,
									border: getCardBorderStyle(t),
									boxShadow:
										t.palette.mode === "dark"
											? "0 1px 0 0 rgba(255,255,255,0.05)"
											: "0 10px 30px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.03)",
									display: "flex",
									flexDirection: "column",
									height: "100%",
									minHeight: 0,
									overflow: "hidden",
								})}
							>
								{/* Header Skeleton */}
								<Box sx={{ mb: 1.5 }}>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1,
											mb: 0.5,
										}}
									>
										<Skeleton
											variant="text"
											width="40%"
											height={32}
											animation="wave"
										/>
										<Skeleton
											variant="rectangular"
											width={60}
											height={24}
											sx={{ borderRadius: 999 }}
											animation="wave"
										/>
									</Box>
									<Skeleton
										variant="text"
										width="60%"
										height={20}
										animation="wave"
									/>
									<Box
										sx={{
											display: "flex",
											justifyContent: "flex-end",
											mt: 0.5,
										}}
									>
										<Skeleton
											variant="text"
											width="35%"
											height={18}
											animation="wave"
										/>
									</Box>
								</Box>

								<Divider sx={{ my: 1.25 }} />

								{/* Limits & Measurement Skeleton */}
								<Box sx={{ mb: 1.25 }}>
									<Skeleton
										variant="text"
										width="45%"
										height={20}
										sx={{ mb: 0.75 }}
										animation="wave"
									/>
									<Box
										sx={(t) => ({
											borderRadius: 2,
											border: getSurfaceBorderStyle(t, "subtle"),
											backgroundColor:
												t.palette.mode === "dark"
													? alpha(t.palette.background.paper, 0.35)
													: t.palette.grey[50],
											px: 1.5,
											py: 1.25,
											display: "grid",
											gridTemplateColumns: "auto 1fr auto",
											alignItems: "center",
											gap: 1.5,
										})}
									>
										<Box>
											<Skeleton
												variant="text"
												width={40}
												height={16}
												animation="wave"
											/>
											<Skeleton
												variant="text"
												width={50}
												height={24}
												sx={{ mt: 0.2 }}
												animation="wave"
											/>
										</Box>
										<Box sx={{ display: "flex", justifyContent: "center" }}>
											<Skeleton
												variant="rectangular"
												width={120}
												height={56}
												sx={{ borderRadius: 2 }}
												animation="wave"
											/>
										</Box>
										<Box sx={{ textAlign: "right" }}>
											<Skeleton
												variant="text"
												width={40}
												height={16}
												animation="wave"
											/>
											<Skeleton
												variant="text"
												width={50}
												height={24}
												sx={{ mt: 0.2 }}
												animation="wave"
											/>
										</Box>
									</Box>
								</Box>

								{/* Notes Skeleton */}
								<Grid container spacing={1.5} sx={{ mb: 1.25 }}>
									<Grid item xs={12} md={5}>
										<Skeleton
											variant="text"
											width="30%"
											height={20}
											sx={{ mb: 0.75 }}
											animation="wave"
										/>
										<Skeleton
											variant="rectangular"
											width="100%"
											height={80}
											sx={{ borderRadius: 1 }}
											animation="wave"
										/>
									</Grid>
									<Grid item xs={12} md={7}>
										<Skeleton
											variant="text"
											width="35%"
											height={20}
											sx={{ mb: 0.75 }}
											animation="wave"
										/>
										<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
											<Skeleton
												variant="rectangular"
												width={100}
												height={36}
												sx={{ borderRadius: 1 }}
												animation="wave"
											/>
											<Skeleton
												variant="rectangular"
												width={100}
												height={36}
												sx={{ borderRadius: 1 }}
												animation="wave"
											/>
										</Box>
									</Grid>
								</Grid>

								<Divider sx={{ my: 0.5 }} />

								{/* Chart Skeleton */}
								<Box
									sx={(t) => ({
										flex: 1,
										minHeight: 0,
										display: "flex",
										flexDirection: "column",
										overflow: "hidden",
										borderRadius: 2,
										border: getSurfaceBorderStyle(t, "subtle"),
										backgroundColor:
											t.palette.mode === "dark"
												? alpha(t.palette.background.paper, 0.2)
												: "#fff",
									})}
								>
									<Box
										sx={{
											px: { xs: 1.5, sm: 2 },
											py: 1.25,
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											borderBottom: (t) =>
												getSurfaceBorderStyle(t, "subtle"),
										}}
									>
										<Skeleton
											variant="text"
											width={180}
											height={24}
											animation="wave"
										/>
										<Skeleton
											variant="text"
											width={100}
											height={18}
											animation="wave"
										/>
									</Box>
									<Box
										sx={{ flex: 1, minHeight: 0, p: { xs: 1.25, sm: 1.75 } }}
									>
										<Skeleton
											variant="rectangular"
											width="100%"
											height="100%"
											sx={{ borderRadius: 1 }}
											animation="wave"
										/>
									</Box>
								</Box>
							</Paper>
						</Grid>

						{/* Right Side - List Skeleton */}
						<Grid
							item
							xs={12}
							md={4}
							sx={{
								display: "flex",
								flexDirection: "column",
								minHeight: 0,
								height: "100%",
							}}
						>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									height: "100%",
									minHeight: 0,
								}}
							>
								<Paper
									sx={(t) => ({
										flex: 1,
										minHeight: 0,
										borderRadius: 2,
										border: getCardBorderStyle(t),
										boxShadow:
											t.palette.mode === "dark"
												? "0 1px 0 0 rgba(255,255,255,0.04)"
												: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
										display: "flex",
										flexDirection: "column",
										overflow: "hidden",
									})}
								>
									{/* Header */}
									<Box
										sx={{
											px: 1.5,
											py: 1,
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											flexShrink: 0,
										}}
									>
										<Skeleton
											variant="text"
											width={140}
											height={20}
											animation="wave"
										/>
										<Skeleton
											variant="text"
											width={50}
											height={16}
											animation="wave"
										/>
									</Box>
									<Divider />
									{/* List Items */}
									<Box sx={{ flex: 1, minHeight: 0, p: 1.5 }}>
										<Stack spacing={1}>
											{Array.from({ length: 5 }).map((_, index) => (
												<Box
													key={index}
													sx={{
														px: 1.5,
														py: 1,
														borderRadius: 0,
														borderBottom: "1px solid",
														borderColor: "divider",
													}}
												>
													<Grid container spacing={0.75}>
														<Grid item xs={9}>
															<Stack spacing={0.5}>
																<Box
																	sx={{
																		display: "flex",
																		alignItems: "center",
																		gap: 0.5,
																	}}
																>
																	<Skeleton
																		variant="rectangular"
																		width={50}
																		height={20}
																		sx={{ borderRadius: 999 }}
																		animation="wave"
																	/>
																	<Skeleton
																		variant="text"
																		width="60%"
																		height={20}
																		animation="wave"
																	/>
																</Box>
																<Skeleton
																	variant="text"
																	width="80%"
																	height={18}
																	animation="wave"
																/>
																<Skeleton
																	variant="text"
																	width="70%"
																	height={16}
																	animation="wave"
																/>
															</Stack>
														</Grid>
														<Grid item xs={3}>
															<Stack spacing={0.5} alignItems="flex-end">
																<Skeleton
																	variant="text"
																	width="90%"
																	height={16}
																	animation="wave"
																/>
																<Skeleton
																	variant="text"
																	width="60%"
																	height={16}
																	animation="wave"
																/>
															</Stack>
														</Grid>
													</Grid>
												</Box>
											))}
										</Stack>
									</Box>
								</Paper>
							</Box>
						</Grid>
					</Grid>
				</DialogContent>
			) : error ? (
				<DialogContent
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						minHeight: 200,
						gap: 2,
					}}
				>
					<Typography color="error" variant="h6" sx={{ fontWeight: 600 }}>
						<Translate String="LabMeasurementDialog.Error">
							Failed to load measurement details
						</Translate>
					</Typography>
					<Typography
						color="text.secondary"
						variant="body2"
						sx={{ textAlign: "center" }}
					>
						{getErrorMessage(
							error,
							"Please try again later or contact support if the problem persists.",
						)}
					</Typography>
					<Button
						variant="outlined"
						onClick={handleClose}
						startIcon={<CloseIcon />}
					>
						Close
					</Button>
				</DialogContent>
			) : (
				<DialogContent
					sx={{
						bgcolor: "background.default",
						px: { xs: 2, sm: 2.5 },
						pt: 2,
						pb: 0,
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					}}
				>
					<Grid
						container
						spacing={1.5}
						sx={{ height: "100%", minHeight: 0, flex: 1 }}
					>
						<Grid
							item
							xs={12}
							md={8}
							sx={{
								display: "flex",
								flexDirection: "column",
								minHeight: 0,
								height: "100%",
							}}
						>
							{selectedItem ? (
								<>
									<LabMeasurementDetails
										data={selectedItem}
										measurementAnalyzeIdData={measurementAnalyzeIdData}
										editableMeasurement={editableMeasurement}
										setEditableMeasurement={setEditableMeasurement}
										editableTestDate={editableTestDate}
										setEditableTestDate={setEditableTestDate}
										editableComment={editableComment}
										setEditableComment={setEditableComment}
										editableSaveDate={editableSaveDate}
										setEditableSaveDate={setEditableSaveDate}
									/>
									{/* <Box sx={{ padding: 2 }}>
										<Typography variant="h6">Comments</Typography>
										Display CommentForm for adding new comment
										<CommentForm
											labMeasurementId={measurementIdData.LabMeasurementId}
											setComments={setComments}
										/>
										Display list of comments
										{isLoading ? (
											<Typography>Loading comments...</Typography>
										) : !measurementIdData.Comments ? (
											<Typography color="error">
												Failed to load comments
											</Typography>
										) : (
											<CommentList
												comments={measurementIdData.Comments}
												timezone={timezone}
											/>
										)}
									</Box> */}
								</>
							) : (
								<Paper
									sx={(t) => ({
										flex: 1,
										minHeight: 0,
										display: "flex",
										flexDirection: "column",
										borderRadius: 2.5,
										border: getCardBorderStyle(t),
										boxShadow:
											t.palette.mode === "dark"
												? "0 1px 0 0 rgba(255,255,255,0.05)"
												: "0 10px 30px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.03)",
										backgroundImage:
											t.palette.mode === "dark"
												? "none"
												: "linear-gradient(to bottom, rgba(0,0,0,0.015), rgba(0,0,0,0))",
									})}
								>
									<Box
										sx={{
											flex: 1,
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											minHeight: 0,
											p: { xs: 3, sm: 4 },
										}}
									>
										<Box
											sx={(t) => ({
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												gap: 2,
												p: { xs: 2.5, sm: 3 },
												borderRadius: 2,
												backgroundColor:
													t.palette.mode === "dark"
														? alpha(t.palette.background.paper, 0.3)
														: alpha(t.palette.primary.main, 0.04),
												border: `1px dashed ${
													t.palette.mode === "dark"
														? getSurfaceBorderColor(t, "subtle")
														: alpha(t.palette.primary.main, 0.2)
												}`,
												maxWidth: 400,
												width: "100%",
											})}
										>
											<Box
												sx={(t) => ({
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													width: 56,
													height: 56,
													borderRadius: "50%",
													backgroundColor:
														t.palette.mode === "dark"
															? alpha(t.palette.primary.main, 0.15)
															: alpha(t.palette.primary.main, 0.1),
													color: t.palette.primary.main,
													mb: 0.5,
												})}
											>
												<InfoOutlinedIcon sx={{ fontSize: 28 }} />
											</Box>
											<Typography
												variant="h6"
												sx={{
													fontWeight: 700,
													color: "text.primary",
													textAlign: "center",
												}}
											>
												<Translate String="LabMeasurementDialog.SelectTitle">
													Measurement Details
												</Translate>
											</Typography>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{
													textAlign: "center",
													lineHeight: 1.6,
												}}
											>
												<Translate String="LabMeasurementDialog.Select">
													Select a measurement from the list on the right to see
													details.
												</Translate>
											</Typography>
										</Box>
									</Box>
								</Paper>
							)}
						</Grid>
						<Grid
							item
							xs={12}
							md={4}
							sx={{
								display: "flex",
								flexDirection: "column",
								minHeight: 0,
								height: "100%",
							}}
						>
							<MeasurementList
								measurements={measurementAnalyzeIdData}
								onSelect={handleListItemClick}
								selectedLabMeasurementId={LabMeasurementIdIs}
							/>
						</Grid>
					</Grid>
				</DialogContent>
			)}

			<DialogActions
				sx={{
					px: { xs: 2, sm: 2.5 },
					py: 1.5,
					borderTop: 1,
					borderColor: "divider",
					bgcolor:
						theme.palette.mode === "dark"
							? alpha(theme.palette.common.white, 0.03)
							: "grey.50",
					display: "flex",
					justifyContent: "flex-end",
					gap: 1.5,
					flexWrap: "wrap",
				}}
			>
				<CloseActionButton
					onClick={handleClose}
					disabled={isMeasurementActionPending}
				>
					<Translate String="Procedure.Button.Close">Close</Translate>
				</CloseActionButton>
				<DeleteActionButton
					onClick={handleDeleteClick}
					loading={isDeletingMeasurement}
					loadingText={
						<Translate String="LabDaily.Deleting">Deleting...</Translate>
					}
					disabled={isMeasurementActionPending || !selectedItem}
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.LabMeasurementDialog.L1302C17.DeleteMeasurement", "Delete measurement")}
				>
					<Translate String="Shift.Event.Delete">Delete</Translate>
				</DeleteActionButton>
				<SaveActionButton
					onClick={handleSaveClick}
					loading={isSavingMeasurement}
					loadingText={
						<Translate String="LabDaily.Saving">Saving...</Translate>
					}
					disabled={isMeasurementActionPending || !selectedItem}
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.LabMeasurementDialog.L1313C17.SaveMeasurement", "Save measurement")}
				>
					<Translate String="Tasks.Task.Comment.Save">Save</Translate>
				</SaveActionButton>
			</DialogActions>
		</Dialog>
	);
};

const LabMeasurementDetails: React.FC<LabMeasurementDetailsProps> = ({
	data,
	measurementAnalyzeIdData,
	editableMeasurement,
	setEditableMeasurement,
	editableTestDate,
	setEditableTestDate,
	editableComment,
	setEditableComment,
	editableSaveDate,
	setEditableSaveDate,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const { timezone } = useTime();
	const theme = useTheme();
	const [showStatistics, setShowStatistics] = useLocalStorage<boolean>(
		StorageKeys.labMeasurementStatisticsVisible,
		true,
	);

	const handleToggleStatistics = useCallback(() => {
		setShowStatistics((v) => !v);
	}, [setShowStatistics]);

	useEffect(() => {
		if (data) {
			setEditableMeasurement(data.Measurement ?? "");
			setEditableTestDate(data.TestDate);
			setEditableComment(data.Comment);
			setEditableSaveDate(data.SaveDate);
		}
	}, [
		data,
		setEditableMeasurement,
		setEditableTestDate,
		setEditableComment,
		setEditableSaveDate,
	]);
	const user = useContext(UserContext);
	const { data: users } = useFetchUsers(user?.CompanyId);
	const getUserNameById = useCallback(
		(userId: number) => {
			if (!users || !userId) return "Unknown User";
			const userFound = users.find((u) => u.Id === userId);
			return userFound ? userFound.Name : "Unknown User";
		},
		[users],
	);

	// Memoize date formatting to avoid recalculation on every render
	const sampleTakenInputValue = useMemo(() => {
		if (!editableTestDate) return "";
		try {
			return dayjs
				.utc(editableTestDate)
				.tz(timezone)
				.format("YYYY-MM-DDTHH:mm");
		} catch {
			return "";
		}
	}, [editableTestDate, timezone]);

	const saveDateInputValue = useMemo(() => {
		if (!editableSaveDate) return "";
		try {
			return dayjs
				.utc(editableSaveDate)
				.tz(timezone)
				.format("YYYY-MM-DDTHH:mm");
		} catch {
			return "";
		}
	}, [editableSaveDate, timezone]);

	return (
		<Paper
			sx={(t) => ({
				p: { xs: 2, sm: 2.5 },
				borderRadius: 2.5,
				border: getCardBorderStyle(t),
				boxShadow:
					t.palette.mode === "dark"
						? "0 1px 0 0 rgba(255,255,255,0.05)"
						: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minHeight: 0,
				overflow: "hidden",
			})}
		>
			{/* Header */}
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
					gap: { xs: 1.25, md: 2 },
					alignItems: "start",
					mb: 1.5,
				}}
			>
				<Box sx={{ minWidth: 0 }}>
					<Box
						sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
					>
						<Typography
							variant="h6"
							sx={{
								fontWeight: 800,
								lineHeight: 1.2,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								minWidth: 0,
							}}
						>
							{data.TagDescription || data.GroupName}
						</Typography>

						<Chip
							label={`Tag ${data.TagId}`}
							size="small"
							sx={{
								flexShrink: 0,
								height: 24,
								borderRadius: 999,
								fontWeight: 700,
								bgcolor:
									theme.palette.mode === "dark"
										? alpha(theme.palette.primary.main, 0.18)
										: alpha(theme.palette.primary.main, 0.08),
								color:
									theme.palette.mode === "dark"
										? theme.palette.primary.light
										: theme.palette.primary.dark,
								border: "1px solid",
								borderColor: alpha(theme.palette.primary.main, 0.2),
							}}
						/>
					</Box>

					<Typography
						variant="body2"
						color="text.secondary"
						sx={{
							mt: 0.25,
							fontWeight: 600,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{data.ParameterName}
					</Typography>
				</Box>

				<Typography
					variant="caption"
					color="text.secondary"
					sx={{
						textAlign: { xs: "left", md: "right" },
						lineHeight: 1.35,
						whiteSpace: "nowrap",
					}}
				>
					<Translate String="LabMeasurementDialog.By.User">
						Performed by:
					</Translate>{" "}
					{getUserNameById(data?.UserId)}{" "}
					<Box component="span" sx={{ mx: 0.75, opacity: 0.6 }}>
						•
					</Box>
					<Translate String="LabDaily.SaveDate">Saved:</Translate>{" "}
					{dayjs(data.SaveDate).tz(timezone).format("DD/MM/YY HH:mm:ss")}
				</Typography>
			</Box>

			<Divider sx={{ my: 1.25 }} />

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					gap: 1.25,
				}}
			>
				{/* Top controls */}
				<Grid container spacing={1.5} alignItems="stretch">
					{/* Limits + Value */}
					<Grid item xs={12} md={7}>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{
								textTransform: "uppercase",
								fontWeight: 700,
								letterSpacing: "0.6px",
								mb: 0.75,
								display: "block",
							}}
						>
							<Translate String="LabMeasurementDialog.LimitsAndValue">
								Limits &amp; measurement
							</Translate>
						</Typography>

						<Box
							sx={(t) => ({
								borderRadius: 2,
								border: getSurfaceBorderStyle(t, "subtle"),
								backgroundColor: t.palette.background.paper,
								px: 1.5,
								py: 1.25,
								display: "grid",
								gridTemplateColumns: "auto 1fr auto",
								alignItems: "center",
								gap: 1.5,
							})}
						>
							{/* Min */}
							<Box sx={{ textAlign: "left" }}>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontWeight: 700 }}
								>
									<Translate String="Auto.Features.Lab.Ui.Components.LabMeasurementDialog.L1548C10.Min">Min</Translate>
								</Typography>
								<Typography sx={{ mt: 0.2, fontWeight: 800 }}>
									{data.MinLimit}
								</Typography>
							</Box>

							{/* Value */}
							<Box sx={{ display: "flex", justifyContent: "center" }}>
								<Box sx={{ width: "100%", maxWidth: 160 }}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											fontWeight: 700,
											textAlign: "center",
											display: "block",
											mb: 0.5,
										}}
									>
										<Translate String="Auto.Features.Lab.Ui.Components.LabMeasurementDialog.L1568C11.Measurement">Measurement</Translate>
									</Typography>

									<TextField
										type="text"
										value={editableMeasurement}
										onChange={(e) => {
											const value = e.target.value;
											const validInput = /^[+-]?\d*[.,]?\d*$/.test(value);
											if (!validInput) return;
											setEditableMeasurement(value);
										}}
										inputProps={{
											min: data.MinLimit,
											max: data.MaxLimit,
											autoComplete: "off",
											style: { textAlign: "center", padding: "8px 10px" },
										}}
										size="small"
										fullWidth
										sx={(t) => {
											const parsedValue = parseFloat(
												String(editableMeasurement).replace(",", "."),
											);
											const within =
												!isNaN(parsedValue) &&
												parsedValue >= data.MinLimit &&
												parsedValue <= data.MaxLimit;

											// calmer: color via border/text, not full fill
											const accent = within
												? t.palette.success.main
												: t.palette.error.main;

											return {
												"& .MuiOutlinedInput-root": {
													fontWeight: 900,
													borderRadius: 2,
													"& fieldset": {
														borderWidth: 2,
														borderColor: alpha(accent, 0.35),
													},
													"&:hover fieldset": {
														borderColor: alpha(accent, 0.6),
													},
													"&.Mui-focused fieldset": {
														borderColor: accent,
													},
												},
												"& input": { color: accent },
											};
										}}
									/>

									<Typography
										variant="caption"
										sx={(t) => {
											const parsedValue = parseFloat(
												String(editableMeasurement).replace(",", "."),
											);
											const within =
												!isNaN(parsedValue) &&
												parsedValue >= data.MinLimit &&
												parsedValue <= data.MaxLimit;

											return {
												mt: 0.5,
												display: "block",
												textAlign: "center",
												color: within
													? t.palette.success.main
													: t.palette.error.main,
												fontWeight: 700,
											};
										}}
									>
										{(() => {
											const parsedValue = parseFloat(
												String(editableMeasurement).replace(",", "."),
											);
											if (isNaN(parsedValue)) return "Enter a number";
											return parsedValue >= data.MinLimit &&
												parsedValue <= data.MaxLimit
												? "Within range"
												: "Out of range";
										})()}
									</Typography>
								</Box>
							</Box>

							{/* Max */}
							<Box sx={{ textAlign: "right" }}>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontWeight: 700 }}
								>
									<Translate String="Auto.Features.Lab.Ui.Components.LabMeasurementDialog.L1665C10.Max">Max</Translate>
								</Typography>
								<Typography sx={{ mt: 0.2, fontWeight: 800 }}>
									{data.MaxLimit}
								</Typography>
							</Box>
						</Box>
					</Grid>

					{/* Note */}
					<Grid item xs={12} md={5}>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{
								textTransform: "uppercase",
								fontWeight: 700,
								letterSpacing: "0.6px",
								mb: 0.75,
								display: "block",
							}}
						>
							<Translate String="LabDaily.Note">Notes</Translate>
						</Typography>

						<TextField
							fullWidth
							variant="outlined"
							value={editableComment}
							onChange={(e) => setEditableComment(e.target.value)}
							placeholder={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.LabMeasurementDialog.L1696C20.AddContextOptional", "Add context (optional)")}
							multiline
							minRows={4}
							size="small"
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: 2,
									backgroundColor: "background.paper",
									alignItems: "flex-start",
									"& fieldset": { borderWidth: 1.5 },
								},
							}}
						/>
					</Grid>

					{/* Dates bar */}
					<Grid item xs={12}>
						<Box
							sx={(t) => ({
								display: "grid",
								gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
								gap: 1.25,
								px: 1.5,
								py: 1.1,
								borderRadius: 2,
								backgroundColor: t.palette.background.paper,
								border: getSurfaceBorderStyle(t, "subtle"),
							})}
						>
							<Box
								sx={{
									display: "flex",
									gap: 1,
									alignItems: "baseline",
									minWidth: 0,
								}}
							>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{
										fontWeight: 800,
										textTransform: "uppercase",
										letterSpacing: "0.6px",
										flexShrink: 0,
									}}
								>
									<Translate String="LabMeasurementDialog.SampleTaken">
										Sample taken
									</Translate>
								</Typography>
								<TextField
									type="datetime-local"
									size="small"
									value={sampleTakenInputValue || ""}
									onChange={(e) => {
										const date = dayjs(e.target.value);
										if (date.isValid()) {
											const zoned = date.tz(timezone, true);
											setEditableTestDate(zoned.utc().toISOString());
										}
									}}
									InputLabelProps={{ shrink: true }}
									sx={{
										minWidth: 0,
										"& .MuiOutlinedInput-root": {
											borderRadius: 2,
											backgroundColor: "background.paper",
										},
										"& .MuiInputBase-input": {
											padding: "4px 8px",
										},
									}}
								/>
							</Box>

							<Box
								sx={{
									display: "flex",
									gap: 1,
									alignItems: "baseline",
									justifyContent: { sm: "flex-end" },
									minWidth: 0,
								}}
							>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{
										fontWeight: 800,
										textTransform: "uppercase",
										letterSpacing: "0.6px",
										flexShrink: 0,
									}}
								>
									<Translate String="LabDaily.SaveDate">Saved</Translate>
								</Typography>
								<TextField
									type="datetime-local"
									size="small"
									value={saveDateInputValue || ""}
									disabled
									InputLabelProps={{ shrink: true }}
									sx={{
										minWidth: 0,
										"& .MuiOutlinedInput-root": {
											borderRadius: 2,
											backgroundColor: "background.paper",
										},
										"& .MuiInputBase-input": {
											padding: "4px 8px",
										},
									}}
								/>
							</Box>
						</Box>
					</Grid>
				</Grid>

				{/* Chart */}
				<Divider sx={{ my: 0.5 }} />

				<Box
					sx={(t) => ({
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
						borderRadius: 2,
						border: getSurfaceBorderStyle(t, "subtle"),
						backgroundColor:
							t.palette.mode === "dark"
								? alpha(t.palette.background.paper, 0.18)
								: "#fff",
						boxShadow:
							t.palette.mode === "dark"
								? "0 1px 0 0 rgba(255,255,255,0.04)"
								: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
					})}
				>
					<Box
						sx={{
							px: { xs: 1.5, sm: 2 },
							py: 1.25,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							borderBottom: (t) =>
								getSurfaceBorderStyle(t, "subtle"),
						}}
					>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
							<Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
								<Translate String="Auto.Features.Lab.Ui.Components.LabMeasurementDialog.L1849C62.MeasurementHistory">Measurement history</Translate>
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ fontWeight: 500 }}
							>
								{data.ParameterName}
							</Typography>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							{measurementAnalyzeIdData &&
								measurementAnalyzeIdData.length > 0 && (
									<>
										<Chip
											label={`${measurementAnalyzeIdData.length} ${measurementAnalyzeIdData.length === 1 ? "point" : "points"}`}
											size="small"
											sx={{
												height: 24,
												fontWeight: 600,
												borderRadius: 999,
												bgcolor: "background.paper",
												border: "1px solid",
												borderColor: "divider",
											}}
										/>
										<Tooltip
											title={
												showStatistics ? "Hide statistics" : "Show statistics"
											}
										>
											<IconButton
												size="small"
												onClick={handleToggleStatistics}
												sx={{
													width: 28,
													height: 28,
													color: "text.secondary",
													"&:hover": {
														backgroundColor: "action.hover",
													},
												}}
											>
												{showStatistics ? (
													<ExpandLessIcon fontSize="small" />
												) : (
													<ExpandMoreIcon fontSize="small" />
												)}
											</IconButton>
										</Tooltip>
									</>
								)}
						</Box>
					</Box>

					{/* Statistics Cards */}
					<Collapse in={showStatistics}>
						{measurementAnalyzeIdData &&
							measurementAnalyzeIdData.length > 0 &&
							(() => {
								// Calculate statistics safely
								const measurements = measurementAnalyzeIdData
									.map((m) => m.Measurement)
									.filter(
										(m): m is number =>
											m != null && !isNaN(m) && typeof m === "number",
									);

								const latest =
									measurementAnalyzeIdData[measurementAnalyzeIdData.length - 1]
										?.Measurement;
								const min =
									measurements.length > 0 ? Math.min(...measurements) : null;
								const max =
									measurements.length > 0 ? Math.max(...measurements) : null;
								const avg =
									measurements.length > 0
										? measurements.reduce((a, b) => a + b, 0) /
											measurements.length
										: null;
								const withinLimits =
									latest != null &&
									!isNaN(latest) &&
									latest >= data.MinLimit &&
									latest <= data.MaxLimit;

								return (
									<Box
										sx={{
											px: { xs: 1.5, sm: 2 },
											py: 1.25,
											display: "grid",
											gridTemplateColumns: {
												xs: "repeat(2, 1fr)",
												sm: "repeat(4, 1fr)",
											},
											gap: 1.25,
											borderBottom: (t) =>
												getSurfaceBorderStyle(t, "subtle"),
										}}
									>
										<Box>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{
													fontWeight: 600,
													textTransform: "uppercase",
													letterSpacing: "0.5px",
												}}
											>
												Latest
											</Typography>
											<Typography
												sx={{
													mt: 0.25,
													fontWeight: 700,
													color: withinLimits ? "success.main" : "error.main",
												}}
											>
												{latest?.toFixed(2) || "—"}
											</Typography>
										</Box>
										<Box>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{
													fontWeight: 600,
													textTransform: "uppercase",
													letterSpacing: "0.5px",
												}}
											>
												Average
											</Typography>
											<Typography
												sx={{
													mt: 0.25,
													fontWeight: 700,
													color: "text.primary",
												}}
											>
												{avg?.toFixed(2) || "—"}
											</Typography>
										</Box>
										<Box>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{
													fontWeight: 600,
													textTransform: "uppercase",
													letterSpacing: "0.5px",
												}}
											>
												Min
											</Typography>
											<Typography
												sx={{
													mt: 0.25,
													fontWeight: 700,
													color: "success.main",
												}}
											>
												{min?.toFixed(2) || "—"}
											</Typography>
										</Box>
										<Box>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{
													fontWeight: 600,
													textTransform: "uppercase",
													letterSpacing: "0.5px",
												}}
											>
												Max
											</Typography>
											<Typography
												sx={{ mt: 0.25, fontWeight: 700, color: "error.main" }}
											>
												{max?.toFixed(2) || "—"}
											</Typography>
										</Box>
									</Box>
								);
							})()}
					</Collapse>

					<Box sx={{ flex: 1, minHeight: 0, p: { xs: 1.25, sm: 1.75 } }}>
						<LineChartWithLimits data={measurementAnalyzeIdData} />
					</Box>
				</Box>
			</Box>
		</Paper>
	);
};

const MeasurementList: React.FC<MeasurementListProps> = ({
	measurements,
	onSelect,
	selectedLabMeasurementId: selectedLabMeasurementIdProp,
}) => {
	const user = useContext(UserContext);
	const { data: users } = useFetchUsers(user?.CompanyId);
	const [selectedLabMeasurementId, setSelectedLabMeasurementId] = useState<
		number | null
	>(selectedLabMeasurementIdProp);
	const selectedItemRef = useRef<HTMLLIElement | null>(null);
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const { timezone } = useTime();
	const theme = useTheme();

	const getUserNameById = useCallback(
		(userId: number) => {
			if (!users || !userId) {
				return "Unknown User";
			}

			const matchedUser = users.find((currentUser) => currentUser.Id === userId);
			return matchedUser?.Name ?? "Unknown User";
		},
		[users],
	);

	// Sync internal state with prop when it changes (e.g., when dialog opens with a measurement)
	useEffect(() => {
		if (
			selectedLabMeasurementIdProp !== null &&
			selectedLabMeasurementIdProp !== undefined
		) {
			setSelectedLabMeasurementId(selectedLabMeasurementIdProp);
		}
	}, [selectedLabMeasurementIdProp]);

	// Scroll to selected item when selection changes
	useEffect(() => {
		if (selectedLabMeasurementId !== null && selectedItemRef.current) {
			const timeoutId = window.setTimeout(() => {
				selectedItemRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});
			}, 100);

			return () => {
				window.clearTimeout(timeoutId);
			};
		}

		return undefined;
	}, [selectedLabMeasurementId]);

	// Handle item selection and apply background change
	const handleItemClick = (LabMeasurementId: number, LabAnalyzeId: number) => {
		setSelectedLabMeasurementId(LabMeasurementId); // Set selected item ID
		onSelect(LabMeasurementId, LabAnalyzeId); // Call parent function with selected IDs
	};

	if (!measurements || measurements.length === 0) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "100%",
					minHeight: 0,
				}}
			>
				<Paper
					sx={(t) => ({
						flex: 1,
						minHeight: 0,
						borderRadius: 2,
						border: getCardBorderStyle(t),
						boxShadow:
							t.palette.mode === "dark"
								? "0 1px 0 0 rgba(255,255,255,0.04)"
								: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
						backgroundColor: t.palette.background.paper,
					})}
				>
					<Box
						sx={{
							px: 1.5,
							py: 1.1,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							flexShrink: 0,
							bgcolor:
								theme.palette.mode === "dark"
									? alpha(theme.palette.common.white, 0.03)
									: "grey.50",
						}}
					>
						<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
							<Translate String="LabMeasurementDialog.History">
								Measurement history
							</Translate>
						</Typography>
						<Typography variant="caption" color="text.secondary">
							<Translate String="Auto.Features.Lab.Ui.Components.LabMeasurementDialog.L2155C60.0">0</Translate> <Translate String="LabMeasurementDialog.Items">items</Translate>
						</Typography>
					</Box>
					<Divider />
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							p: { xs: 2, sm: 3 },
						}}
					>
						<Box
							sx={(t) => ({
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: 2,
								p: { xs: 2.5, sm: 3 },
								borderRadius: 2,
								backgroundColor:
									t.palette.mode === "dark"
										? alpha(t.palette.background.paper, 0.3)
										: "#fff",
								border: `1px dashed ${
									t.palette.mode === "dark"
										? getSurfaceBorderColor(t, "subtle")
										: alpha(t.palette.grey[400], 0.3)
								}`,
								maxWidth: 320,
								width: "100%",
							})}
						>
							<Box
								sx={(t) => ({
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: 56,
									height: 56,
									borderRadius: "50%",
									backgroundColor:
										t.palette.mode === "dark"
											? alpha(t.palette.grey[500], 0.15)
											: alpha(t.palette.grey[500], 0.1),
									color:
										t.palette.mode === "dark"
											? t.palette.grey[400]
											: t.palette.grey[600],
									mb: 0.5,
								})}
							>
								<InboxOutlinedIcon sx={{ fontSize: 28 }} />
							</Box>
							<Typography
								variant="h6"
								sx={{
									fontWeight: 700,
									color: "text.primary",
									textAlign: "center",
								}}
							>
								<Translate String="LabMeasurementDialog.EmptyListTitle">
									No measurements found
								</Translate>
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									textAlign: "center",
									lineHeight: 1.6,
								}}
							>
								<Translate String="LabMeasurementDialog.EmptyList">
									There are no measurements available to display.
								</Translate>
							</Typography>
						</Box>
					</Box>
				</Paper>
			</Box>
			);
		}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minHeight: 0,
			}}
		>
			<Paper
				sx={(t) => ({
					flex: 1,
					minHeight: 0,
					borderRadius: 2,
					border: getCardBorderStyle(t),
					boxShadow:
						t.palette.mode === "dark"
							? "0 1px 0 0 rgba(255,255,255,0.04)"
							: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					backgroundColor: t.palette.background.paper,
				})}
			>
				<Box
					sx={{
						px: 1.5,
						py: 1.1,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexShrink: 0,
						bgcolor:
							theme.palette.mode === "dark"
								? alpha(theme.palette.common.white, 0.03)
								: "grey.50",
					}}
				>
					<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
						<Translate String="LabMeasurementDialog.History">
							Measurement history
						</Translate>
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{measurements?.length || 0}{" "}
						<Translate String="LabMeasurementDialog.Items">items</Translate>
					</Typography>
				</Box>
				<Divider />
				<Box
					ref={scrollContainerRef}
					sx={{
						flex: 1,
						minHeight: 0,
						overflowY: "auto",
						overflowX: "hidden",
						"&::-webkit-scrollbar": {
							width: "8px",
						},
						"&::-webkit-scrollbar-track": {
							backgroundColor: "transparent",
						},
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: (t) =>
								t.palette.mode === "dark"
									? alpha(t.palette.common.white, 0.2)
									: alpha(t.palette.common.black, 0.2),
							borderRadius: "4px",
							"&:hover": {
								backgroundColor: (t) =>
									t.palette.mode === "dark"
										? alpha(t.palette.common.white, 0.3)
										: alpha(t.palette.common.black, 0.3),
							},
						},
					}}
				>
					<List dense disablePadding>
						{measurements.map((item) => (
							<ListItem
								key={item.LabMeasurementId}
								ref={(el) => {
									if (item.LabMeasurementId === selectedLabMeasurementId) {
										selectedItemRef.current = el;
									}
								}}
								onClick={() =>
									handleItemClick(item.LabMeasurementId, item.LabAnalyzeId)
								}
								sx={{
									px: 1.5,
									py: 1.1,
									borderRadius: 0,
									borderBottom: "1px solid",
									borderColor: theme.palette.divider,
									backgroundColor:
										item.LabMeasurementId === selectedLabMeasurementId
											? alpha(theme.palette.primary.main, 0.06)
											: "transparent",
									cursor: "pointer",
									transition:
										"background-color 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
									"&:hover": {
										backgroundColor:
											item.LabMeasurementId === selectedLabMeasurementId
												? alpha(theme.palette.primary.main, 0.1)
												: theme.palette.action.hover,
									},
									"&:last-of-type": {
										borderBottom: "none",
									},
								}}
							>
								<Grid container spacing={0.75}>
									{/* Left Column */}
									<Grid item xs={9}>
										<Stack spacing={0.5}>
											<Typography
												variant="subtitle2"
												sx={{
													fontWeight: 600,
													display: "flex",
													alignItems: "center",
													gap: 0.5,
												}}
											>
												<Box
													component="span"
												sx={{
													fontWeight: 700,
													px: 0.75,
													py: 0.25,
													borderRadius: 999,
													backgroundColor: alpha(
														theme.palette.primary.main,
														0.08,
													),
													color: theme.palette.primary.main,
													border: "1px solid",
													borderColor: alpha(theme.palette.primary.main, 0.18),
												}}
											>
												{item.TagId}
												</Box>
												<Typography
													component="span"
													variant="body2"
													color="text.secondary"
													sx={{
														whiteSpace: "nowrap",
														textOverflow: "ellipsis",
														overflow: "hidden",
													}}
												>
													{item.TagDescription}
												</Typography>
											</Typography>

											<Typography variant="body2">
												{item.ParameterName}:{" "}
												{item.Measurement != null && (
													<>
														<Box
															component="span"
														sx={{
															px: 1,
															py: 0.25,
															ml: 1,
															borderRadius: 999,
															fontWeight: 600,
															color:
																item.Measurement < item.MinLimit ||
																item.Measurement > item.MaxLimit
																	? "error.dark"
																	: "success.dark",
															backgroundColor:
																item.Measurement < item.MinLimit ||
																item.Measurement > item.MaxLimit
																	? alpha(theme.palette.error.main, 0.12)
																	: alpha(theme.palette.success.main, 0.12),
															border: "1px solid",
															borderColor:
																item.Measurement < item.MinLimit ||
																item.Measurement > item.MaxLimit
																	? alpha(theme.palette.error.main, 0.22)
																	: alpha(theme.palette.success.main, 0.22),
														}}
													>
															{item.Measurement}
														</Box>{" "}
														<Typography
															component="span"
															variant="body2"
															color="text.secondary"
														>
															(Min: {item.MinLimit}, Max: {item.MaxLimit})
														</Typography>
													</>
												)}
											</Typography>

											<Typography variant="caption" color="text.secondary">
												<Translate String="LabDaily.TestDate">Test:</Translate>{" "}
												{dayjs
													.utc(item.TestDate)
													.tz(timezone)
													.format("DD/MM/YY HH:mm:ss")}
											</Typography>

											<Typography variant="caption" color="text.secondary">
												<Translate String="LabDaily.SaveDate">Saved:</Translate>{" "}
												{dayjs
													.utc(item.SaveDate)
													.tz(timezone)
													.format("DD/MM/YY HH:mm:ss")}
											</Typography>
										</Stack>
									</Grid>

									{/* Right Column */}
									<Grid item xs={3}>
										<Stack spacing={0.5} alignItems="flex-end">
											<Typography variant="caption" color="text.secondary">
												<Translate String="LabDaily.User">User:</Translate>{" "}
												{getUserNameById(item.UserId)} –{" "}
												{dayjs
													.utc(item.SaveDate)
													.tz(timezone)
													.format("HH:mm:ss")}
											</Typography>

											{item.Comment && (
												<Typography
													variant="caption"
													color="text.secondary"
													textAlign="right"
												>
													<Translate String="LabDaily.Comment">
														Comment:
													</Translate>{" "}
													{item.Comment}
												</Typography>
											)}
										</Stack>
									</Grid>
								</Grid>
							</ListItem>
						))}
					</List>
				</Box>
			</Paper>
		</Box>
	);
};

export default LabMeasurementDialog;

export const CommentForm: React.FC<CommentFormProps> = ({
	labMeasurementId,
	setComments,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const [newCommentText, setNewCommentText] = useState("");
	const [file, setFile] = useState<File | null>(null);
	const { createComment, isSubmitting } = useMutateCreateComment();
	const currentUser = useContext(UserContext);

	const handleCommentSubmit = async () => {
		if (!newCommentText.trim()) {
			return;
		}

		try {
			const commentData = {
				LabMeasurementId: String(labMeasurementId),
				Comment: newCommentText,
				File: file,
			};

			await createComment(commentData);

			setNewCommentText("");
			setFile(null);

			setComments((prev) => [
				...prev,
				{
					CommentId: 0,
					Comment: newCommentText,
					Date: new Date().toISOString(),
					UserId: currentUser?.Id ?? 0,
					UserName: currentUser?.Name ?? "You",
				},
			]);

			void AlertRequest.success();
		} catch (error: unknown) {
			console.error("Error adding comment:", error);
			void AlertRequest.failed();
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = event.target.files?.[0] ?? null;
		setFile(selectedFile);
	};

	return (
		<div>
			<TextField
				fullWidth
				value={newCommentText}
				onChange={(e) => setNewCommentText(e.target.value)}
				placeholder={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.LabMeasurementDialog.L2557C17.WriteAComment", "Write a comment...")}
				multiline
				rows={4}
				variant="outlined"
				sx={{ mb: 2 }}
			/>
			<div>
				<input
					type="file"
					accept="image/*,application/pdf,.doc,.docx"
					onChange={handleFileChange}
				/>
			</div>
			<IconButton
				color="primary"
				onClick={() => {
					void handleCommentSubmit();
				}}
				disabled={isSubmitting || !newCommentText.trim()}
			>
				<ArrowForwardIcon />
			</IconButton>
		</div>
	);
};

export const CommentList: React.FC<CommentListProps> = ({
	comments,
	timezone,
}) => {
	return (
		<Box>
			{comments.length === 0 ? (
				<Typography variant="body2" color="textSecondary">
					No comments yet.
				</Typography>
			) : (
				comments
					.slice()
					.reverse()
					.map((comment, index) => (
						<Box
							key={comment.CommentId || index}
							sx={{
								display: "flex",
								alignItems: "flex-start",
							p: 2,
							mt: 0.5,
							mb: 0.5,
							border: (t) => getSurfaceBorderStyle(t, "subtle"),
							borderRadius: 2,
								boxShadow: "none",
								gap: 2,
							}}
						>
							{/* Tooltip added to show full name on hover */}
							<Tooltip title={comment.UserName || "Unknown User"}>
								<Avatar {...stringAvatar(comment.UserName || "U")} />
							</Tooltip>
							<Box sx={{ flexGrow: 1 }}>
								<Typography
									variant="body2"
									sx={{ fontWeight: "bold", mb: 1 }}
								></Typography>
								<Typography variant="caption" color="textSecondary">
									{dayjs(comment.Date).tz(timezone).format("DD/MM/YY HH:mm:ss")}
								</Typography>
							</Box>
						</Box>
					))
			)}
		</Box>
	);
};
